import { Effect, Exit, Ref } from "effect";
import type { WorkbenchCore } from "./workbench-core.ts";
import { onlyMissing } from "./workbench-bootstrap.ts";

export const beginNavigation = Effect.fn("Frontend.StationWorkbench.beginNavigation")(function* (core: WorkbenchCore, activeWorkspace?: string) {
  const sequence = yield* Ref.modify(core.state, current => [current.navigationSequence + 1,
    current.closed ? current : { ...current, navigationSequence: current.navigationSequence + 1,
      ...(activeWorkspace === undefined ? {} : { activeWorkspace }) }] as const);
  yield* core.publish; yield* core.queries.cancel; return sequence;
});

export const openWorkbenchWorkspace = Effect.fn("Frontend.StationWorkbench.open")(function* (core: WorkbenchCore, id: string) {
  const before = yield* core.read;
  if (before.stationId === null || !before.booted || !(yield* core.active())) return;
  const stationId = before.stationId;
  const sequence = yield* beginNavigation(core, id), previousActive = before.workspaceId;
  if (!(yield* core.active(sequence))) return;
  const activate = () => core.station.mutate(stationId, current => Effect.succeed(
    current.workspaces.includes(id) ? { ...current, active: id } : current));
  if (id === before.workspaceId) {
    if (id === before.activeWorkspace) return;
    const result = yield* Effect.exit(activate());
    if (!(yield* core.active(sequence))) return;
    if (Exit.isFailure(result) || result.value.active !== id) {
      if (Exit.isSuccess(result)) yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
      return yield* core.io.alert("Failed to update the active workspace in the workerstation session.");
    }
    return yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
  }
  const loading = yield* Effect.exit(Effect.all([core.pending.flush, Effect.exit(core.queries.run(core.api.workspace(id, stationId)))] as const, { concurrency: 2 }));
  if (!(yield* core.active(sequence))) return;
  if (Exit.isFailure(loading)) return yield* core.update(current => ({ ...current, activeWorkspace: previousActive, phase: "error" }));
  const loaded = loading.value[1];
  if (Exit.isFailure(loaded)) {
    if (!onlyMissing(loaded)) {
      yield* core.update(current => ({ ...current, activeWorkspace: previousActive }));
      return yield* core.io.alert("Could not load the workerspace. Please try again.");
    }
    const removed = yield* Effect.exit(core.station.mutate(stationId, current => Effect.succeed({ ...current,
      workspaces: current.workspaces.filter(workspace => workspace !== id) })));
    if (!(yield* core.active(sequence))) return;
    if (Exit.isFailure(removed)) {
      yield* core.update(current => ({ ...current, activeWorkspace: previousActive }));
      return yield* core.io.alert("Workerspace not found, but failed to remove it from the workerstation session.");
    }
    yield* core.io.alert("Workerspace not found — removing it from the sidebar.");
    return yield* core.update(current => ({ ...current, workspaces: removed.value.workspaces, activeWorkspace: removed.value.active }));
  }
  const result = yield* Effect.exit(activate());
  if (!(yield* core.active(sequence))) return;
  if (Exit.isFailure(result) || result.value.active !== id) {
    yield* core.update(current => Exit.isSuccess(result)
      ? { ...current, workspaces: result.value.workspaces, activeWorkspace: result.value.active }
      : { ...current, activeWorkspace: previousActive });
    return yield* core.io.alert("Failed to update the active workspace in the workerstation session.");
  }
  yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
  yield* core.hydrate(yield* core.target(id), loaded.value);
});

export const reloadWorkbenchWorkspace = Effect.fn("Frontend.StationWorkbench.reload")(function* (core: WorkbenchCore, edits: Ref.Ref<number>) {
  const before = yield* core.read;
  if (before.workspaceId === null || before.stationId === null || !(yield* core.active())) return;
  const target = yield* core.target(before.workspaceId), edit = yield* Ref.get(edits), generation = yield* core.persistence.generation(target);
  const result = yield* Effect.exit(core.queries.run(core.api.workspace(before.workspaceId, before.stationId)));
  if (Exit.isFailure(result) || !(yield* core.active(before.navigationSequence)) || (yield* core.read).workspaceId !== before.workspaceId
    || (yield* Ref.get(edits)) !== edit || (yield* core.persistence.generation(target)) !== generation || (yield* core.persistence.isWriting(target))) return;
  const pending = yield* core.pending.read;
  if (pending.pending?.stationId === target.stationId && pending.pending.workspaceId === target.workspaceId) return;
  yield* core.hydrate(target, result.value);
});
